import { useEffect, useState } from 'react';
import { api, type Asset } from '../lib/api';

// ---------------------------------------------------------------------------
// The library, as something you can look at.
//
// Before this it was a table of storage paths in upload order: no name, no
// picture, no way to filter, sort, annotate or remove. Thomas uploaded a series
// of images and a few videos and said he had no control over them, which was
// exactly right — the table had a kind, a key, a byte count and a timestamp,
// and a screen cannot draw what is not there.
//
// 053 added the three columns that make a file recognisable. This draws them.
// ---------------------------------------------------------------------------

export type Kind = Asset['kind'];
export type SortKey = 'newest' | 'oldest' | 'name' | 'largest' | 'kind';

const KIND_LABEL: Record<Kind, string> = {
  image: 'Images', video: 'Video', audio: 'Audio', document: 'Documents',
};

export function basename(key: string): string {
  return key.split('/').pop() ?? key;
}

/**
 * What to call it: the given name, else the file it came from.
 *
 * The fallback reads the STORAGE key and never the media key, and the order
 * matters more than it looks. A storage key ends in the filename the creator
 * uploaded; a media key ends in `<asset-id>.<ext>`, because the promoted copy's
 * destination is derived from the job's tenant rather than from caller-supplied
 * text. Preferring the media key meant every asset displayed as its own uuid
 * from the moment it became usable — the library showing ids where it had shown
 * names, and only for the assets anybody could actually play.
 */
export function displayName(a: Asset): string {
  return a.name?.trim() || basename(a.storage_key) || basename(a.media_key ?? '');
}

export function sizeOf(bytes: number | null): string {
  if (!bytes) return '—';
  const mb = bytes / 1_048_576;
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

/**
 * Sorting, with one deliberate rule: `name` sorts by what is DISPLAYED.
 *
 * Sorting by the `name` column would put every unnamed asset — which is most of
 * them, since naming is new — in one undifferentiated block of nulls while the
 * screen shows filenames in an order that matches nothing. A sort that does not
 * match what the eye reads is worse than no sort.
 */
export function sortAssets(rows: Asset[], by: SortKey): Asset[] {
  const out = [...rows];
  switch (by) {
    case 'name':
      return out.sort((a, b) =>
        displayName(a).localeCompare(displayName(b), undefined, { numeric: true, sensitivity: 'base' }));
    case 'largest':
      return out.sort((a, b) => (b.bytes ?? 0) - (a.bytes ?? 0));
    case 'oldest':
      return out.sort((a, b) => a.created_at.localeCompare(b.created_at));
    case 'kind':
      return out.sort((a, b) => a.kind.localeCompare(b.kind) || displayName(a).localeCompare(displayName(b)));
    default:
      return out.sort((a, b) => b.created_at.localeCompare(a.created_at));
  }
}

/**
 * One asset's thumbnail.
 *
 * Fetched per asset rather than in the listing, because a thumbnail URL is a
 * short-lived bearer credential — returning twenty of them with every listing
 * would mint twenty credentials whether or not anything drew them. Null is the
 * ordinary answer for a document or a video mid-promotion, so it renders a
 * placeholder rather than a broken image.
 */
export function Thumb({ asset, size = 132 }: { asset: Asset; size?: number }) {
  const [url, setUrl] = useState<string | null>(null);
  const [gone, setGone] = useState(false);

  useEffect(() => {
    let live = true;
    setUrl(null); setGone(false);
    if (asset.status !== 'ready') { setGone(true); return; }
    api.assetThumbnail(asset.id)
      .then((u) => { if (live) { setUrl(u); if (!u) setGone(true); } })
      .catch(() => { if (live) setGone(true); });
    return () => { live = false; };
  }, [asset.id, asset.status]);

  const box: React.CSSProperties = {
    width: size, height: Math.round(size * 0.5625), borderRadius: 8,
    background: 'var(--surface-3)', display: 'flex', alignItems: 'center',
    justifyContent: 'center', overflow: 'hidden', flex: 'none',
  };

  if (url) {
    return (
      <div style={box}>
        <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      </div>
    );
  }
  return (
    <div style={box}>
      <span className="sub-dim" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em' }}>
        {gone ? asset.kind : '…'}
      </span>
    </div>
  );
}

/** The category tabs, with counts, so an empty one is visibly empty. */
export function KindTabs({
  assets, value, onChange,
}: { assets: Asset[]; value: Kind | 'all'; onChange: (k: Kind | 'all') => void }) {
  const count = (k: Kind) => assets.filter((a) => a.kind === k).length;
  const tabs: (Kind | 'all')[] = ['all', 'image', 'video', 'audio', 'document'];
  return (
    <div className="row wrap" style={{ gap: 6 }}>
      {tabs.map((k) => {
        const n = k === 'all' ? assets.length : count(k);
        const on = value === k;
        return (
          <button
            key={k}
            className={on ? 'btn pri' : 'btn'}
            onClick={() => onChange(k)}
            // Counts rather than hiding empty categories: "no audio yet" is
            // information, and a tab that vanishes teaches nothing.
            title={n === 0 ? 'nothing here yet' : undefined}
          >
            {k === 'all' ? 'All' : KIND_LABEL[k]} <span className="chip">{n}</span>
          </button>
        );
      })}
    </div>
  );
}

/**
 * Rename an asset and write a note against it.
 *
 * Both optional, and blank means "no value" rather than an empty string — the
 * database refuses a name that is only whitespace, so sending one would be a
 * 400 a person cannot act on. The note is where "this is the b-roll for the
 * flight board segment" lives, which is the thing a filename cannot carry.
 */
export function Details({
  asset, busy, onCancel, onSave, groups,
}: {
  asset: Asset;
  busy: boolean;
  onCancel: () => void;
  onSave: (name: string, notes: string, group: string) => void;
  /** 156: the groups the library already has, for the picker. */
  groups?: string[];
}) {
  const [name, setName] = useState(asset.name ?? '');
  const [notes, setNotes] = useState(asset.notes ?? '');
  const [group, setGroup] = useState(asset.group_name ?? '');

  return (
    <div className="panel" style={{ marginTop: 16 }}>
      <div className="panel-hd">
        <h3>Details</h3>
        <span className="chip">{asset.kind}</span>
      </div>
      <div className="panel-bd">
        <div className="row wrap" style={{ gap: 16, alignItems: 'flex-start' }}>
          <Thumb asset={asset} size={200} />
          <div style={{ flex: 1, minWidth: 260 }}>
            <label className="lbl">Name</label>
            <input
              value={name} disabled={busy} maxLength={200}
              placeholder={displayName(asset)}
              onChange={(e) => setName(e.target.value)}
              style={{ width: '100%' }}
            />
            <div className="sub-dim">
              Blank keeps the filename. The name is what sorting and the scene
              picker read, so it is worth giving the ones you will reuse.
            </div>

            <label className="lbl" style={{ marginTop: 14 }}>Notes</label>
            <textarea
              value={notes} disabled={busy} rows={3} maxLength={4000}
              placeholder="What this is, where it was shot, when to use it."
              onChange={(e) => setNotes(e.target.value)}
              style={{ width: '100%' }}
            />

            <label className="lbl" style={{ marginTop: 14 }}>Group</label>
            <input
              value={group} disabled={busy} maxLength={60} list="asset-groups"
              placeholder="A shelf of your own — Season 3 stills, Sponsor packs, Lower thirds"
              onChange={(e) => setGroup(e.target.value)}
              style={{ width: '100%' }}
            />
            <datalist id="asset-groups">{(groups ?? []).map((g) => <option key={g} value={g} />)}</datalist>

            <div className="sub-dim" style={{ marginTop: 10 }}>
              {sizeOf(asset.bytes)}
              {asset.duration_ms ? ` · ${Math.round(asset.duration_ms / 1000)}s` : ''}
              {' · added '}{new Date(asset.created_at).toLocaleString()}
            </div>

            <div className="row" style={{ gap: 8, marginTop: 14 }}>
              <button className="btn pri" disabled={busy} onClick={() => onSave(name, notes, group)}>
                {busy ? 'Saving…' : 'Save'}
              </button>
              <button className="btn" disabled={busy} onClick={onCancel}>Cancel</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
